import React, { useState } from 'react'
import { useFormikContext } from 'formik'
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AddressFormData, InstallationForOptions, InstallationForOptionsType, PartnerNumberFormFields } from 'src/Libs/installation-statement/types'
import PartnerNumberSection from './PartnerNumberSection'

/**
 * Component to select partner or company for the installation statement.
 */
const PartnerSection: React.FC = () => {
  const { t } = useCustomTranslation()
  const {
    values: { partnerNumber, partnerNumberEnabled },
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormikContext<AddressFormData>()
  const [installationFor, setInstallationFor] = useState<InstallationForOptionsType>(
    partnerNumber ? InstallationForOptions.PARTNER : InstallationForOptions.COMPANY
  )

  const handleInstallationFor = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const value = event.target.value as InstallationForOptionsType
    setInstallationFor(value)

    const isPartnerNumberEnabled = value === InstallationForOptions.PARTNER

    /* set partnerNumber form fields */
    if (isPartnerNumberEnabled) {
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_ENABLED, isPartnerNumberEnabled)
      return
    }

    /* Reset all partner-related fields in a single batch. third parameter false to skip validation */
    await Promise.allSettled([
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER, '', false),
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_ENABLED, false, false),
      setFieldValue(PartnerNumberFormFields.PARTNER_NUMBER_VALID, false, false),
      setFieldTouched(PartnerNumberFormFields.PARTNER_NUMBER, false, false),
    ])
    /* Run validation only once after all fields are reset */
    await validateForm()
  }

  return (
    <Stack spacing={2}>
      {/* installationFor not tracked by formik */}
      <FormControl>
        <RadioGroup name="installationFor" value={installationFor} onChange={handleInstallationFor}>
          <FormControlLabel
            value={InstallationForOptions.COMPANY}
            control={<Radio />}
            label={t('installation-statement.address.installation-for.my-company')}
          />
          <FormControlLabel
            value={InstallationForOptions.PARTNER}
            control={<Radio />}
            label={t('installation-statement.address.installation-for.partner-company')}
          />
        </RadioGroup>
      </FormControl>
      {/* partner number fields tracked by formik */}
      {partnerNumberEnabled && <PartnerNumberSection />}
    </Stack>
  )
}

export default PartnerSection
